import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { db } from "./firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { ChevronDown, ChevronUp, XCircle, Check } from "lucide-react";

function formatDateString(dateObj) {
  if (!dateObj || !(dateObj instanceof Date) || isNaN(dateObj)) return "—";
  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const year = dateObj.getFullYear();
  return `${day}.${month}.${year}`;
}

// DOKŁADNIE TAK JAK W DASHBOARD
const parseDateString = (s) => {
  if (!s) return null;
  if (s.includes(".")) {
    const [day, month, year] = s.split(".");
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  return new Date(s);
};


function parseEntryDate(entry) {
  if (entry.date) return parseDateString(entry.date);
  if (entry.createdAt && entry.createdAt.toDate) return entry.createdAt.toDate();
  return null;
}

function getDayOfWeek(dateObj) {
  const days = [
    "niedziela", "poniedziałek", "wtorek", "środa",
    "czwartek", "piątek", "sobota"
  ];
  if (!dateObj || !(dateObj instanceof Date) || isNaN(dateObj)) return "";
  return days[dateObj.getDay()];
}

const monthNames = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
];
const currentYear = new Date().getFullYear();

const QuestionHistoryModal = ({ fieldName, label, type, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [selectedTab, setSelectedTab] = useState("weekly");

  const today = new Date();
  const isoDay = today.getDay() === 0 ? 7 : today.getDay();
  const defaultMonday = new Date(today);
  defaultMonday.setDate(today.getDate() - (isoDay - 1));
  const defaultMondayStr = defaultMonday.toISOString().split("T")[0];
  const defaultTodayStr = today.toISOString().split("T")[0];
  const [weeklyStart, setWeeklyStart] = useState(defaultMondayStr);
  const [weeklyEnd, setWeeklyEnd] = useState(defaultTodayStr);
  const [confirmedWeeklyStart, setConfirmedWeeklyStart] = useState(defaultMondayStr);
  const [confirmedWeeklyEnd, setConfirmedWeeklyEnd] = useState(defaultTodayStr);

  const [selectedMonthIndex, setSelectedMonthIndex] = useState(today.getMonth());
  const [selectedMonthYear, setSelectedMonthYear] = useState(today.getFullYear().toString());
  const [confirmedMonthIndex, setConfirmedMonthIndex] = useState(today.getMonth());
  const [confirmedMonthYear, setConfirmedMonthYear] = useState(today.getFullYear().toString());

  const [selectedYear, setSelectedYear] = useState(today.getFullYear().toString());
  const [confirmedYear, setConfirmedYear] = useState(today.getFullYear().toString());

  const [sortDesc, setSortDesc] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const q = query(collection(db, "entries"), orderBy("createdAt", "desc"));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      setEntries(data);
    };
    fetchData();
  }, []);

  const getPeriodBoundaries = () => {
    if (selectedTab === "weekly") {
      const start = new Date(confirmedWeeklyStart);
      start.setHours(0, 0, 0, 0);
      const end = new Date(confirmedWeeklyEnd);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    } else if (selectedTab === "monthly") {
      const baseDate = new Date(
        `${confirmedMonthYear}-${("0" + (parseInt(confirmedMonthIndex, 10) + 1)).slice(-2)}-01`
      );
      const start = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
      const end = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0, 23, 59, 59, 999);
      return { start, end };
    } else if (selectedTab === "yearly") {
      const baseDate = new Date(`${confirmedYear}-01-01`);
      const start = new Date(baseDate.getFullYear(), 0, 1);
      const end = new Date(baseDate.getFullYear(), 11, 31, 23, 59, 59, 999);
      return { start, end };
    }
  };

  useEffect(() => {
    const { start, end } = getPeriodBoundaries();
    const filtered = entries
      .filter(entry => entry[fieldName] !== undefined)
      .filter(entry => {
        const entryDate = parseEntryDate(entry);
        if (!entryDate) return false;
        return entryDate >= start && entryDate <= end;
      })
      .sort((a, b) => {
        const dateA = parseEntryDate(a) || new Date(0);
        const dateB = parseEntryDate(b) || new Date(0);
        return sortDesc ? dateB - dateA : dateA - dateB;
      });
    setFilteredEntries(filtered);
  }, [
    entries, fieldName, sortDesc, selectedTab,
    confirmedWeeklyStart, confirmedWeeklyEnd,
    confirmedMonthIndex, confirmedMonthYear,
    confirmedYear
  ]);

  const renderPeriodInfo = () => {
    const { start, end } = getPeriodBoundaries();
    return (
      <div className="text-center text-gray-600 dark:text-gray-300 mb-2 text-sm font-medium">
        Zakres: {formatDateString(start)} – {formatDateString(end)}
      </div>
    );
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
      <div className="w-full max-w-3xl h-[90vh] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-emerald-300 flex flex-col overflow-hidden relative">
        {/* Nagłówek */}
        <div className="flex items-center justify-between px-4 sm:px-8 py-4 border-b border-emerald-100 dark:border-gray-700 bg-emerald-50/80 dark:bg-gray-900/80 sticky top-0 z-30 rounded-t-2xl">
          <div className="flex-1 pr-2">
            <h2 className="text-xl sm:text-2xl font-bold text-emerald-800 dark:text-emerald-200 leading-tight">{label}</h2>
            <div className="text-xs text-gray-700 dark:text-gray-300 mt-1">Historia odpowiedzi</div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-red-100 dark:hover:bg-gray-800 transition ml-2"
            aria-label="Zamknij"
          >
            <XCircle className="text-red-500" size={32} />
          </button>
        </div>

        {/* Zakładki okresu */}
        <div className="flex gap-2 sm:gap-4 bg-white dark:bg-gray-900 border-b border-emerald-100 dark:border-gray-700 px-4 sm:px-8 py-2 sticky top-[72px] z-30">
          <button
            onClick={() => setSelectedTab("weekly")}
            className={`px-4 py-2 rounded-full font-semibold transition border border-gray-300 shadow-sm ${selectedTab === "weekly" ? "bg-green-500 text-white" : "bg-white text-gray-800 dark:bg-gray-700 dark:text-white hover:bg-green-100"}`}>
            Tygodniowy
          </button>
          <button
            onClick={() => setSelectedTab("monthly")}
            className={`px-4 py-2 rounded-full font-semibold transition border border-gray-300 shadow-sm ${selectedTab === "monthly" ? "bg-green-500 text-white" : "bg-white text-gray-800 dark:bg-gray-700 dark:text-white hover:bg-green-100"}`}>
            Miesięczny
          </button>
          <button
            onClick={() => setSelectedTab("yearly")}
            className={`px-4 py-2 rounded-full font-semibold transition border border-gray-300 shadow-sm ${selectedTab === "yearly" ? "bg-green-500 text-white" : "bg-white text-gray-800 dark:bg-gray-700 dark:text-white hover:bg-green-100"}`}>
            Roczny
          </button>
        </div>

        {/* Kontrolki wyboru zakresu */}
        <div className="bg-white dark:bg-gray-900 px-4 sm:px-8 py-4 flex flex-col gap-3 border-b border-emerald-100 dark:border-gray-700">
          <div className="flex flex-wrap gap-2 sm:gap-4 items-center justify-center">
            {selectedTab === "weekly" && (
              <>
                <label className="text-green-700 font-medium flex flex-col text-xs">
                  Początek
                  <input
                    type="date"
                    value={weeklyStart}
                    onChange={(e) => setWeeklyStart(e.target.value)}
                    className="mt-1 px-4 py-2 border rounded focus:outline-none dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600"
                    max={weeklyEnd}
                  />
                </label>
                <label className="text-green-700 font-medium flex flex-col text-xs">
                  Koniec
                  <input
                    type="date"
                    value={weeklyEnd}
                    onChange={(e) => setWeeklyEnd(e.target.value)}
                    className="mt-1 px-4 py-2 border rounded focus:outline-none dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600"
                    min={weeklyStart}
                    max={defaultTodayStr}
                  />
                </label>
                <button
                  onClick={() => {
                    setConfirmedWeeklyStart(weeklyStart);
                    setConfirmedWeeklyEnd(weeklyEnd);
                  }}
                  className="mt-4 sm:mt-6 p-2 bg-green-500 rounded-full text-white"
                  title="Zatwierdź zakres"
                >
                  <Check size={20} />
                </button>
              </>
            )}

            {selectedTab === "monthly" && (
              <>
                <label className="text-green-700 font-medium flex flex-col text-xs">
                  Miesiąc
                  <select
                    value={selectedMonthIndex}
                    onChange={(e) => setSelectedMonthIndex(parseInt(e.target.value, 10))}
                    className="mt-1 px-4 py-2 border rounded focus:outline-none dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600"
                  >
                    {monthNames.map((m, i) => (
                      <option key={i} value={i}>{m}</option>
                    ))}
                  </select>
                </label>
                <label className="text-green-700 font-medium flex flex-col text-xs">
                  Rok
                  <select
                    value={selectedMonthYear}
                    onChange={(e) => setSelectedMonthYear(e.target.value)}
                    className="mt-1 px-4 py-2 border rounded focus:outline-none dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600"
                  >
                    {Array.from({ length: 10 }, (_, i) => currentYear - i).map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </label>
                <button
                  onClick={() => {
                    setConfirmedMonthIndex(selectedMonthIndex);
                    setConfirmedMonthYear(selectedMonthYear);
                  }}
                  className="mt-4 sm:mt-6 p-2 bg-green-500 rounded-full text-white"
                  title="Zatwierdź zakres"
                >
                  <Check size={20} />
                </button>
              </>
            )}

            {selectedTab === "yearly" && (
              <>
                <label className="text-green-700 font-medium flex flex-col text-xs">
                  Rok
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="mt-1 px-4 py-2 border rounded focus:outline-none dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600"
                  >
                    {Array.from({ length: 10 }, (_, i) => currentYear - i).map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </label>
                <button
                  onClick={() => setConfirmedYear(selectedYear)}
                  className="mt-4 sm:mt-6 p-2 bg-green-500 rounded-full text-white"
                  title="Zatwierdź zakres"
                >
                  <Check size={20} />
                </button>
              </>
            )}
          </div>
          {renderPeriodInfo()}
          <div className="flex justify-end items-center">
            <button
              onClick={() => setSortDesc(!sortDesc)}
              className="flex items-center text-xs text-emerald-600 dark:text-emerald-200 hover:underline"
              title="Zmień sortowanie"
            >
              {sortDesc ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              {sortDesc ? " Najpierw najnowsze" : " Najpierw najstarsze"}
            </button>
          </div>
        </div>

        {/* Lista odpowiedzi */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4 bg-white dark:bg-gray-900">
          {filteredEntries.length === 0 ? (
            <div className="text-gray-600 dark:text-gray-300 py-16 text-center text-lg">
              Brak odpowiedzi w wybranym okresie.
            </div>
          ) : (
            <ul className="divide-y divide-emerald-100 dark:divide-gray-700 max-w-2xl mx-auto">
              {filteredEntries.map((entry, idx) => (
                <li key={entry.id || idx} className="py-5">
                  <div className="font-semibold text-emerald-800 dark:text-emerald-100 mb-1">
                    📅 {formatDateString(parseEntryDate(entry))} ({getDayOfWeek(parseEntryDate(entry))})
                  </div>
                  <div className={
                    "text-gray-800 dark:text-gray-200 text-base" +
                    (type === "textarea" ? " whitespace-pre-line" : "") +
                    (!entry[fieldName] ? " text-gray-400 italic" : "")
                  }>
                    {type === "checkbox"
                      ? (entry[fieldName] ? "✅ Tak" : "❌ Nie")
                      : type === "number"
                      ? entry[fieldName]
                      : entry[fieldName] || "—"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QuestionHistoryModal;
